import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DanyAvatar, Icon, useViewport } from "@/ds";
import { useAuth } from "@/contexts/AuthContext";
import { useUnreadMessages } from "@/hooks/unreadCounts";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { modeForPath } from "@/lib/danyPageMode";
import { TabBarContext } from "@/components/nav/TabBarContext";
import { useHideTabBar } from "@/hooks/useHideTabBar";
import { tabsForSession, activeTabKey, MENU_TAB } from "@/components/nav/tabs";
import MobileMenuSheet from "@/components/nav/MobileMenuSheet";

/**
 * The mobile navigation shell, below 992px (T12-M1).
 *
 * Mounted once at the root, outside the router, so it survives navigation and
 * keeps the sheet open across a route change. It replaces three things that are
 * all gone below this width: the Navbar's hamburger, the dashboard's black top
 * bar with its drawer, and the bottom-right floating Dany button.
 *
 * Every measurement here comes from SPEC_NAVEGACION_MOVIL and is written as the
 * literal the spec gives, not re-derived: 64px of bar over the safe area, a
 * 76px centre column, a 60px FAB whose centre sits exactly on the bar's top
 * edge, and a 74px cradle behind it showing only its top half. Colours are
 * tokens.
 *
 * The one gold thing on the screen is the FAB. The active tab is marked with a
 * sand pip, never with yellow.
 */

/** The bar's height, above the safe area. It is a token and not a number
 *  because index.css reserves the same height at the bottom of every page:
 *  one declaration in fixydany.css, two readers, and no way for them to drift
 *  apart (T12-M5). */
const BAR_H = "var(--tabbar-height)";
const CENTER_COL = 76;

/** The cradle's notch: the bar's 1px top rule is masked away under the FAB so
 *  the hairline does not cut across the circle. Same radius as the mockup. */
const RULE_MASK = "radial-gradient(circle 37px at 50% 0, transparent 98%, #000 100%)";

/** Keyboard focus, on the pip. The spec's one use of gold outside the FAB, and
 *  it is a focus ring, not a state colour. */
const FOCUS_RING = "0 0 0 4px rgba(255,222,23,.35)";

function Badge({ count }) {
  if (!count) return null;
  return (
    <span
      aria-hidden="true"
      data-testid="tabbar-badge"
      style={{
        position: "absolute", top: 0, left: 32,
        minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9,
        background: "var(--danger)", color: "var(--paper)",
        font: "700 11px/18px var(--font-mono)", textAlign: "center",
        boxShadow: "0 0 0 2px var(--paper)",
      }}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function Tab({ tab, active, label, badge, badgeLabel, onNavigate }) {
  const [pressed, setPressed] = useState(false);
  // Keyboard focus only. `:focus-visible` is what tells a tap apart from a Tab
  // key; without it the ring would flash on every touch.
  const [focused, setFocused] = useState(false);
  // The whole cell is the touch target — 64px tall, well over --touch-min.
  return (
    <Link
      to={tab.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      /* With a count the visible text alone reads "Messages" and drops the
         number, so the spec spells it out: "Mensajes, 3 sin leer". Without one
         the link's own text is already the name. */
      aria-label={badge ? badgeLabel : undefined}
      data-testid={`tab-${tab.key}`}
      data-active={active ? "true" : "false"}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerCancel={() => setPressed(false)}
      onFocus={(e) => setFocused(e.target.matches(":focus-visible"))}
      onBlur={() => { setPressed(false); setFocused(false); }}
      style={{
        outline: "none",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "flex-start", paddingTop: 8, gap: 4,
        minWidth: 0, position: "relative", textDecoration: "none",
        color: active ? "var(--worker)" : "var(--smoke-light)",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <span
        style={{
          width: 56, height: 30, borderRadius: 15,
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative",
          background: active ? "var(--sand)" : "transparent",
          transform: pressed ? "scale(.97)" : "scale(1)",
          boxShadow: focused ? FOCUS_RING : "none",
          transition:
            "background-color 200ms var(--ease-out), transform 150ms var(--ease-out)",
        }}
      >
        <Icon name={tab.icon} size={24} strokeWidth={2} />
        <Badge count={badge} />
      </span>
      <span
        style={{
          font: `${active ? 750 : 600} 12px/16px var(--font-display)`,
          fontStretch: "88%", letterSpacing: "-.01em", whiteSpace: "nowrap",
          color: active ? "var(--worker)" : "var(--smoke)",
        }}
      >
        {label}
      </span>
    </Link>
  );
}

/** The pages that carry no app shell: both sign-up doors and the phone code. */
export function isAuthFlowPath(pathname = "") {
  return /^\/signup(\/|$)/.test(pathname) || pathname === "/onboarding"
    || pathname === "/provider/verify-phone";
}

export default function MobileTabBar() {
  const { isNavMobile, width } = useViewport();
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  /* Unread MESSAGES, not notifications (T14-M2). The cell is called Mensajes
     and it counts messages: a quote arriving used to light it, and opening the
     conversation — the only errand the cell names — could not put it out.
     Jorge's decision of 2026-09-22; the bell keeps the other channel. */
  const unread = useUnreadMessages();
  const reduced = usePrefersReducedMotion();
  const ctx = useContext(TabBarContext);
  const hidden = !!ctx?.hidden;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fabPressed, setFabPressed] = useState(false);
  const [menuFocused, setMenuFocused] = useState(false);

  const role = user?.profile?.role;
  const isAdmin = user?.profile?.is_admin || role === "admin";
  const tabs = useMemo(
    () => tabsForSession({ isLoggedIn: !!user, role }),
    [user, role]
  );

  // Admin is an internal console, not a product surface — it keeps the layout
  // it has (DashboardLayout's own drawer), the same reasoning that already
  // keeps the Dany FAB off `/admin` in lib/danyPageMode.
  const onAdmin = location.pathname.startsWith("/admin");
  // And the exclusion is published like any other hide (AUDIT.md 65.6): the
  // body's bottom padding reads TabBarProvider's state, not this return, so
  // without the request an admin page kept ~110px of reserve for a bar that
  // was not drawn.
  useHideTabBar(!!(isAdmin && onAdmin));
  // Sign-up and the phone code are one-errand screens (REG, 2026-10-28): a
  // tab bar under them is five ways out of a form a person is halfway
  // through, and on a phone it covers the button that finishes it.
  const onAuthFlow = isAuthFlowPath(location.pathname);
  useHideTabBar(onAuthFlow);
  if (!isNavMobile || (isAdmin && onAdmin) || onAuthFlow) return null;

  const active = sheetOpen
    ? MENU_TAB
    : activeTabKey(location.pathname, tabs, !!location.state?.fromPanel);

  /**
   * The one label that outgrows its column, swapped for the short form the
   * spec provides — but at 377px, not at the 340px the spec names.
   *
   * **Measured, because the spec's arithmetic does not hold in this app.** It
   * says "Cotizaciones" clears its column with 6px to spare at 360px thanks to
   * `font-stretch: 88%`. That property is inert here: the app loads five STATIC
   * instances of Bricolage Grotesque, every one of them registered at
   * `stretch: 100%`, so there is no `wdth` axis to compress. Measured in the
   * built app at 12px, 88% and 75% both render "Cotizaciones" at 73.1px (600)
   * and 75.2px (750, the active weight) — against a column of (360−76)/4 = 71px.
   * It overflows by 4.2px at 360, and only fits from 377px up.
   *
   * So the threshold is the real fit boundary rather than the assumed one. It
   * keeps the spec's two hard rules — no truncated label, no type below 12px —
   * using the spec's own device and its own copy. The `fontStretch` declaration
   * stays on the label: it costs nothing and becomes correct the day the
   * variable font is loaded, at which point this number goes back to 340.
   * Flagged for Jorge in the T12 report; loading a variable font is a
   * typography change across every screen, not a decision for this component.
   */
  const narrow = width < 377;
  const labelFor = (tab) =>
    tab.key === "quotes" && narrow ? t("nav.tabs.quotesShort") : t(tab.labelKey);

  const openDany = () => {
    setSheetOpen(false);
    navigate("/dany", { state: { mode: modeForPath(location.pathname) } });
  };

  return (
    <>
      {/* Hidden by STATE, never by scroll: the thread and a voice call ask for
          it through useHideTabBar. 200ms out, 240ms back with 60ms of delay —
          the bar should not race the screen that is leaving. */}
      <nav
        aria-label={t("nav.tabs.primary")}
        data-testid="mobile-tabbar"
        data-hidden={hidden ? "true" : "false"}
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 45,
          height: `calc(${BAR_H} + env(safe-area-inset-bottom, 0px))`,
          fontFamily: "var(--font-display)",
          /* Reduced motion takes the displacement out and keeps the fade, at
             150ms in both directions — the spec says so for every transition in
             this shell, not only Dany's. */
          transform: reduced || !hidden ? "translateY(0)" : "translateY(100%)",
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : "auto",
          visibility: hidden ? "hidden" : "visible",
          transition: reduced
            ? (hidden
                ? "opacity 150ms linear, visibility 0s linear 150ms"
                : "opacity 150ms linear, visibility 0s")
            : (hidden
                ? "transform 200ms var(--ease-in-out), opacity 200ms var(--ease-in-out), visibility 0s linear 200ms"
                : "transform 240ms var(--ease-out) 60ms, opacity 240ms var(--ease-out) 60ms, visibility 0s"),
        }}
      >
        {/* Surface + the hairline, masked where the cradle interrupts it. */}
        <div
          style={{
            position: "absolute", inset: 0,
            background: "var(--paper)",
            boxShadow: "0 -6px 20px rgba(38,35,42,.06)",
          }}
        >
          <span
            style={{
              content: "''", position: "absolute", left: 0, right: 0, top: 0,
              height: 1, background: "var(--sand)",
              WebkitMaskImage: RULE_MASK, maskImage: RULE_MASK,
            }}
          />
        </div>

        {/* The cradle: a 74px circle in paper, clipped to its top half so it
            reads as the bar swelling up around the FAB rather than a disc. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute", left: "50%", top: -37, width: 74, height: 74,
            marginLeft: -37, borderRadius: "50%",
            background: "var(--paper)", border: "1px solid var(--sand)",
            boxShadow: "0 -6px 20px rgba(38,35,42,.06)",
            clipPath: "inset(-30px -30px 37px -30px)",
          }}
        />

        <div
          style={{
            position: "absolute", left: 0, right: 0, top: 0, height: BAR_H,
            display: "grid",
            gridTemplateColumns: `minmax(0,1fr) minmax(0,1fr) ${CENTER_COL}px minmax(0,1fr) minmax(0,1fr)`,
          }}
        >
          {tabs.slice(0, 2).map((tab) => (
            <Tab
              key={tab.key} tab={tab} active={active === tab.key}
              label={labelFor(tab)}
              badge={tab.badge ? unread : 0}
              badgeLabel={t("nav.tabs.unread", { label: labelFor(tab), count: unread })}
              onNavigate={() => setSheetOpen(false)}
            />
          ))}

          {/* The FAB's column. The button itself is positioned against the bar,
              not this cell, so it can overhang the top edge. */}
          <div />

          {tabs.slice(2).map((tab) => (
            <Tab
              key={tab.key} tab={tab} active={active === tab.key}
              label={labelFor(tab)}
              badge={tab.badge ? unread : 0}
              badgeLabel={t("nav.tabs.unread", { label: labelFor(tab), count: unread })}
              onNavigate={() => setSheetOpen(false)}
            />
          ))}

          <button
            type="button"
            onClick={() => setSheetOpen((v) => !v)}
            aria-expanded={sheetOpen}
            aria-haspopup="dialog"
            aria-current={active === MENU_TAB ? "page" : undefined}
            data-testid="tab-menu"
            data-active={active === MENU_TAB ? "true" : "false"}
            onFocus={(e) => setMenuFocused(e.target.matches(":focus-visible"))}
            onBlur={() => setMenuFocused(false)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "flex-start", paddingTop: 8, gap: 4,
              minWidth: 0, border: "none", background: "transparent",
              cursor: "pointer", font: "inherit", outline: "none",
              color: active === MENU_TAB ? "var(--worker)" : "var(--smoke-light)",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span
              style={{
                width: 56, height: 30, borderRadius: 15,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: active === MENU_TAB ? "var(--sand)" : "transparent",
                boxShadow: menuFocused ? FOCUS_RING : "none",
                transition: "background-color 200ms var(--ease-out)",
              }}
            >
              <Icon name="menu" size={24} strokeWidth={2} />
            </span>
            <span
              style={{
                font: `${active === MENU_TAB ? 750 : 600} 12px/16px var(--font-display)`,
                fontStretch: "88%", letterSpacing: "-.01em", whiteSpace: "nowrap",
                color: active === MENU_TAB ? "var(--worker)" : "var(--smoke)",
              }}
            >
              {t("dashLayout.menu")}
            </span>
          </button>
        </div>

        {/* Dany. The one gold element on the screen. */}
        <button
          type="button"
          onClick={openDany}
          onPointerDown={() => setFabPressed(true)}
          onPointerUp={() => setFabPressed(false)}
          onPointerCancel={() => setFabPressed(false)}
          onBlur={() => setFabPressed(false)}
          aria-label={t("dany.fab.label")}
          data-testid="tabbar-dany-fab"
          style={{
            position: "absolute", left: "50%", top: -30,
            width: 60, height: 60, marginLeft: -30, borderRadius: "50%",
            border: "none", padding: 0, cursor: "pointer",
            background: "var(--hive)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transform: fabPressed ? "scale(.97)" : "scale(1)",
            boxShadow: fabPressed
              ? "0 0 0 1.5px var(--worker), 0 0 0 7px rgba(255,222,23,.35)"
              : "0 0 0 1.5px var(--worker), 0 4px 12px rgba(38,35,42,.18)",
            transition: "transform 150ms var(--ease-out), box-shadow 150ms var(--ease-out)",
          }}
        >
          {/* 46px, not the spec's 56 (T13-M4) — Jorge's call after Bloque A,
              and a deliberate deviation from SPEC_NAVEGACION_MOVIL §"FAB Dany"
              ("Arte | DanyAvatar en estado idle, 56px (su viewBox trae aire
              propio)").

              The parenthetical is what turned out to be false. Measured, the
              idle art spans 62.08 of the 64 viewBox units across — 97% — and
              its antenna balls sit 1.20x the viewBox half-width from centre,
              so no circle inscribed in that viewBox can contain them. At 56px
              the silhouette was 90.5% of the 60px disc and reached 5.3px
              OUTSIDE it: the antennae over the cradle, which is what Daniel
              and Jorge saw.

              46px puts the whole silhouette inside the disc with ~1.6px of air
              and leaves the body's centre 3.4px below the disc's — the same
              place it sits today, so nothing regresses on the axis Jorge cares
              about. His ~80-85% band is not reachable together with
              containment: the only size in it (50.75px) needs the art centred
              on its full bounding box, which drops the face to 7.4px low —
              worse than today, and the thing he explicitly ruled out. Flagged
              in the tanda report with the whole trade-off curve.

              The -1px is the optical centring: the art is not centred on its
              bounding box, whose middle sits high because the antennae pull it
              there, but between that and the body mass — which is what stops
              the face reading as fallen. */}
          <span style={{ display: "flex", marginTop: -1 }}>
            <DanyAvatar size={46} state="idle" halo={false} />
          </span>
        </button>
        <span
          aria-hidden="true"
          style={{
            position: "absolute", left: "50%", top: 42,
            transform: "translateX(-50%)",
            font: "750 12px/16px var(--font-display)", color: "var(--worker)",
            pointerEvents: "none",
          }}
        >
          Dany
        </span>
      </nav>

      <MobileMenuSheet open={sheetOpen} onClose={() => setSheetOpen(false)} reduced={reduced} />
    </>
  );
}
